import axios from 'axios'
import { API_BASE_URL } from './apiutils'
import { withGetDedupe, clearInflight } from './requestDedupe'

const BUSINESS_SCOPE_TOKEN_KEY = 'business_scope_token'

/**
 * 產生 UUID v4（用於 idempotency key）。
 * 優先使用 crypto.randomUUID()；不支援時以 Math.random() fallback。
 */
export function generateIdempotencyKey () {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // fallback（舊版瀏覽器）
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

/** 需要附加 Idempotency Key 的 HTTP 方法 */
const IDEMPOTENCY_METHODS = new Set(['post', 'put', 'patch', 'delete'])

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
})

/** 登出後導向登入頁的 callback，由 AuthContext 註冊 */
let logoutHandler = null

export function setLogoutHandler (fn) {
  logoutHandler = fn
}

/**
 * 寫入切店憑證。
 *
 * sessionStorage = 這個分頁當下的店（實際發請求用的，分頁之間天生隔離）
 * localStorage   = 新分頁的預設店（只用來記住「最後選過哪家」）
 *
 * 兩邊都寫，但意義不同：前者只影響當前分頁，後者只影響之後新開的分頁。
 * 已經開著的其他分頁讀自己的 sessionStorage，不會被這次切換影響。
 */
export function setBusinessScopeToken (token) {
  // 切店／登出時，把進行中的 GET 紀錄清掉，避免新店共用到舊店的請求
  clearInflight()
  try {
    if (token) {
      sessionStorage.setItem(BUSINESS_SCOPE_TOKEN_KEY, token)
      localStorage.setItem(BUSINESS_SCOPE_TOKEN_KEY, token)
    } else {
      // 登出／清除時兩邊都要清，否則下次開分頁會拿到已失效的舊憑證
      sessionStorage.removeItem(BUSINESS_SCOPE_TOKEN_KEY)
      localStorage.removeItem(BUSINESS_SCOPE_TOKEN_KEY)
    }
  } catch (e) {
    // ignore storage failures
  }
}

/**
 * 讀取這個分頁的切店憑證。
 *
 * ⚠️ 一定要以 sessionStorage 為準。改成直接讀 localStorage 的話，
 * 多分店店主開兩個分頁時，其中一邊切店會把另一邊也切走 ——
 * 而且畫面上的店名不會跟著變，等於在錯誤的店裡操作卻毫無察覺。
 *
 * 分頁第一次讀到空值時，才從 localStorage 種一份進來（延續上次選的店）。
 * 種完之後這個分頁就定住了，別的分頁再怎麼切都不會動到它。
 */
export function getBusinessScopeToken () {
  try {
    const scoped = sessionStorage.getItem(BUSINESS_SCOPE_TOKEN_KEY)
    if (scoped) return scoped

    const lastUsed = localStorage.getItem(BUSINESS_SCOPE_TOKEN_KEY)
    if (lastUsed) sessionStorage.setItem(BUSINESS_SCOPE_TOKEN_KEY, lastUsed)
    return lastUsed
  } catch (e) {
    return null
  }
}

/**
 * 從 API 錯誤中取出要顯示給使用者的訊息（含後端回傳的 message 與 errors 陣列）
 * @param {Error} error - axios 或 API 拋出的錯誤
 * @param {string} fallback - 預設訊息
 * @returns {string}
 */
export function getApiErrorMessage (error, fallback = '操作失敗，請稍後再試') {
  const data = error?.response?.data
  const msg = data?.message
  const errs = Array.isArray(data?.errors) ? data.errors : []
  const errText = errs.filter(Boolean).join('、')
  if (errText && msg) return `${msg}：${errText}`
  if (errText) return errText
  if (msg) return msg
  if (error?.message) return error.message
  return fallback
}

function isAuthFailure (errOrRes) {
  const isError = errOrRes instanceof Error || errOrRes?.isAxiosError
  if (isError && errOrRes?.response?.status === 401) return true
  const d = (isError ? errOrRes?.response?.data : errOrRes?.data) || {}
  return d?.success === false && d?.isAuthenticated === false
}

function isRefreshRequest (config) {
  const url = config?.url || ''
  return String(url).indexOf('/api/auth/refresh-token') !== -1
}

let refreshPromise = null

async function refreshTokens () {
  if (refreshPromise) return refreshPromise
  refreshPromise = api
    .post('/api/auth/refresh-token', {})
    .then((res) => {
      refreshPromise = null
      return res.data
    })
    .catch((err) => {
      refreshPromise = null
      throw err
    })
  return refreshPromise
}

async function logoutAndRedirect () {
  try {
    await (typeof logoutHandler === 'function' ? logoutHandler() : Promise.resolve())
  } catch (e) {
    /* 忽略登出失敗 */
  } finally {
    const path = typeof window !== 'undefined' ? window.location.pathname : ''
    const to = path.startsWith('/admin') ? '/admin/login' : '/login'
    if (typeof window !== 'undefined') window.location.href = to
  }
}

/** 千金／合作夥伴訂閱查詢 API（需 subscription_view JWT，不可套用商家後台 token） */
function isSubscriptionViewApi (config) {
  const url = String(config?.url || '')
  if (!url.includes('/api/subscription/')) return false
  return !url.includes('/api/subscription/login')
}

function getSubscriptionViewToken () {
  try {
    return sessionStorage.getItem('subscription_view_token')
  } catch (e) {
    return null
  }
}

api.interceptors.request.use(
  (config) => {
    // 訂閱查詢後台：使用專用 token（type: subscription_view），避免被商家 cookie 覆蓋
    if (isSubscriptionViewApi(config)) {
      const subToken = getSubscriptionViewToken()
      if (subToken) {
        config.headers['Authorization'] = `Bearer ${subToken}`
      } else {
        delete config.headers['Authorization']
      }
    } else {
      const businessScopeToken = getBusinessScopeToken()
      if (businessScopeToken) {
        // 多店模式下，永遠優先使用 scoped token，避免被舊 cookie token 覆蓋
        config.headers['Authorization'] = `Bearer ${businessScopeToken}`
      } else {
        const token = document.cookie.replace(
          /(?:(?:^|.*;\s*)token\s*=\s*([^;]*).*$)|^.*$/,
          '$1'
        )
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`
        }
      }
    }

    // ── Idempotency Key ──────────────────────────────────────────────────────
    // 對所有寫入請求自動附加 X-Idempotency-Key。
    // 設計：key 在每次 request config 建立時產生（即每次呼叫 api.post/put/patch 時），
    // 而非在 button click 時產生。這樣可正確防止：
    //   1. axios 內部對同一 config 物件的 retry（同一 config → 同一 key → 後端攔截）
    //   2. 程式碼層面意外呼叫兩次相同 API（兩次各自建立 config → 各自有 key）
    // 按鈕連點由前端 submitting state guard 處理（見各 handleSubmit）。
    const method = (config.method || '').toLowerCase()
    if (IDEMPOTENCY_METHODS.has(method) && !config.headers['X-Idempotency-Key']) {
      config.headers['X-Idempotency-Key'] = generateIdempotencyKey()
    }
    // ────────────────────────────────────────────────────────────────────────

    // 實例預設 Content-Type: application/json 會破壞 FormData（需由瀏覽器帶 multipart boundary）
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const h = config.headers
      if (h && typeof h.delete === 'function') {
        h.delete('Content-Type')
      } else if (h) {
        delete h['Content-Type']
        delete h['content-type']
      }
    }
    return config
  },
  (error) => Promise.reject(error)
)

api.interceptors.response.use(
  async (res) => {
    if (isRefreshRequest(res.config)) return res
    if (isSubscriptionViewApi(res.config)) return res
    if (!isAuthFailure(res) || res.config._retry) return res
    res.config._retry = true
    try {
      await refreshTokens()
      return api(res.config)
    } catch (e) {
      await logoutAndRedirect()
      return Promise.reject(e)
    }
  },
  async (error) => {
    const config = error?.config
    if (isRefreshRequest(config)) return Promise.reject(error)
    if (isSubscriptionViewApi(config)) return Promise.reject(error)
    if (!isAuthFailure(error) || config?._retry) return Promise.reject(error)
    config._retry = true
    try {
      await refreshTokens()
      return api(config)
    } catch (refreshErr) {
      await logoutAndRedirect()
      return Promise.reject(refreshErr)
    }
  }
)

// GET in-flight 去重：同一支 API 還在路上時，重複呼叫共用同一個請求。
// 只影響 GET，不做快取；要跳過請在 config 加 dedupe: false。
withGetDedupe(api, getBusinessScopeToken)

export default api
